import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import {
  ackFunnelCheck,
  type AckReason,
  FUNNEL_LABELS,
  FUNNEL_PAGE_TYPES,
  type FunnelCheckResult,
  type FunnelPageRow,
  type FunnelPageType,
  getFunnelCheck,
  getFunnelSettings,
  type KiBefund,
  type ProduktReferenzZeile,
  publishFunnelStrecke,
  type RegelBefund,
  startFunnelCheck,
  type StreckeFehlerDetails,
  type StreckePublishResponse,
} from './api';
import { GefahrBestaetigung, NurAdmin, useIstAdmin } from '../../../components/marketingForm';
import { fetchData } from '../../../helpers/apiUtils';
import {
  ConfirmDialog,
  formatBerlin,
  formatUtcAsBerlin,
  Loading,
  shortHash,
  WarningList,
} from './ui';

/**
 * Reiter „Pruefung & Publizieren" (Spec § 4): die ganze Strecke wird als EIN Schnappschuss geprueft
 * und publiziert.
 *
 * Gate-UX (der Server ist die Grenze — 422 Regel-Fehler, 409 Pruefung fehlt / Hash veraltet /
 * KI rot ohne Quittung; die UI spiegelt nur):
 *  - Regel-Fehler → Publizieren gesperrt, ohne Ausnahme.
 *  - KI rot → „Trotzdem publizieren" erst nach ausdruecklicher Bestaetigung; die schickt die
 *    Quittung `ki_findings` fuer GENAU diesen Check, dann den Publish.
 *  - KI-Fehlversuch (serverseitig gespeichert) → eigener Weg mit Quittung `ki_unavailable`.
 *  - Jede Aenderung an Seiten/Einstellungen ergibt einen neuen Hash: GET /check liefert dann null,
 *    es muss neu geprueft werden.
 * Publish-Warnungen werden angezeigt (der alte Dialog verwarf sie).
 */

/** Spec § 4: KI-Timeout 120 s — etwas Luft fuer Regeln + Netz, dann gilt der Lauf als haengend. */
const MAX_WARTEN_S = 150;

/** Erklaerung je Fehlercode von POST /publish (routes/lpFunnel/check.ts → StreckeFehler.details.code). */
const PUBLISH_FEHLER_HINWEIS: Record<string, string> = {
  regel_fehler: 'Regel-Fehler im geprüften Stand — beheben, neu prüfen, dann veröffentlichen.',
  ki_rot:
    'Die KI hat Stellen zum Korrigieren gefunden — korrigieren oder mit „Trotzdem veröffentlichen“ bestätigen.',
  pruefung_fehlt:
    'Für genau diesen Stand gibt es keine gültige Prüfung — „Strecke prüfen“ starten.',
  hash_geaendert: 'Die Strecke wurde seit der Prüfung geändert — bitte erneut prüfen.',
};

interface PublishFehler {
  text: string;
  details: StreckeFehlerDetails | null;
}
const POLL_MS = 3000;

const SCHWERE_LABEL: Record<RegelBefund['schwere'], string> = {
  fehler: 'Fehler',
  warnung: 'Warnung',
  info: 'Info',
};
const SCHWERE_SEVERITY: Record<RegelBefund['schwere'], 'error' | 'warning' | 'info'> = {
  fehler: 'error',
  warnung: 'warning',
  info: 'info',
};
// Kurz-Etiketten in Marketing-Sprache (30.10., Felix: „Punkt 3 weiß ich nicht mal was das heißen soll“).
const KATEGORIE_LABEL: Record<KiBefund['kategorie'], string> = {
  widerspruch: 'Passt nicht zusammen',
  sprache: 'Tippfehler / Satz',
  fremd: 'Anderes Webinar oder Produkt',
  skript: 'Seitenskript',
};

const REFERENZ_LABEL: Record<ProduktReferenzZeile['slot'], string> = {
  ausgabe: 'Ausgabe',
  quickstart: 'Quickstart',
};

/**
 * „Referenzen der KI-Pruefung: Anmeldeseite, Ausgabe („datei.pdf", vollstaendig), Quickstart …"
 * (Spec 2026-10-08-produkt-medien § 13). null = keine Produkt-Unterlagen hinterlegt.
 */
export function referenzZeile(refs: ProduktReferenzZeile[] | null): string {
  const teile = ['Anmeldeseite'];
  for (const r of refs ?? []) {
    const angaben: string[] = [];
    if (r.dateiName) angaben.push(`„${r.dateiName}“`);
    if (r.seitenGelesen != null && r.seiten != null && r.seitenGelesen < r.seiten)
      angaben.push(`nur ${r.seitenGelesen} von ${r.seiten} Seiten gelesen`);
    angaben.push(r.gekuerzt ? 'gekürzt' : 'vollständig');
    teile.push(`${REFERENZ_LABEL[r.slot] ?? r.slot} (${angaben.join(', ')})`);
  }
  if (!refs?.length)
    teile.push(
      'keine Produkt-Unterlagen hinterlegt (Schritt 3 „Bilder (Produkt-Medien)“ → PDF hochladen verbessert die Prüfung)',
    );
  return `Referenzen der KI-Prüfung: ${teile.join(', ')}`;
}

type Seite = FunnelPageType | 'strecke';
const SEITEN: Seite[] = [...FUNNEL_PAGE_TYPES, 'strecke'];
const seiteLabel = (s: Seite) => (s === 'strecke' ? 'Ganze Strecke' : FUNNEL_LABELS[s]);

interface Props {
  webinarId: string;
  pages: Record<FunnelPageType, FunnelPageRow | null>;
  /** Wechselt nach jedem Speichern im Dialog → letzten Check neu holen (Hash hat sich geaendert). */
  reloadKey: number;
  onPublished: (label: string, warnings: string[]) => void;
  /** Meldet den Prüfstand des aktuellen Strecken-Stands nach oben (Schrittleiste des Dialogs). */
  onCheckChange?: (check: FunnelCheckResult | null) => void;
}

export default function CheckPanel({
  webinarId,
  pages,
  reloadKey,
  onPublished,
  onCheckChange,
}: Props) {
  const istAdmin = useIstAdmin();
  /** Referenz der KI in Worten — nur lesend, Fehler still (die Pruefung haengt nicht daran). */
  const [bezug, setBezug] = useState<KiBezug | null>(null);
  const [check, setCheck] = useState<FunnelCheckResult | null | undefined>(undefined);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const startedAt = useRef<number | null>(null);
  const [publishBusy, setPublishBusy] = useState(false);
  const [publishError, setPublishError] = useState<PublishFehler | null>(null);
  /** Aktueller Strecken-Stand laut Server (GET check.scopeHash) und der letzte Check ueberhaupt. */
  const [aktuellerHash, setAktuellerHash] = useState<string | null>(null);
  const [letzterCheck, setLetzterCheck] = useState<FunnelCheckResult | null>(null);
  /**
   * Feste Regeln, wie der Server sie JETZT rechnet (GET …/check `regeln`). Ein Teil der Regeln haengt an
   * Daten ausserhalb des Pruef-Hashes (Texte anderer Webinare, `fremdes_webinar`) — der gespeicherte Check
   * zeigte sonst einen alten Stand, waehrend der Publish frisch rechnet.
   */
  const [frischeRegeln, setFrischeRegeln] = useState<RegelBefund[] | null>(null);
  /** undefined = Server liefert das Feld (noch) nicht → Zeile weglassen. */
  const [produktReferenz, setProduktReferenz] = useState<ProduktReferenzZeile[] | null | undefined>(
    undefined,
  );
  const [published, setPublished] = useState<StreckePublishResponse | null>(null);
  const [ackDialog, setAckDialog] = useState<AckReason | null>(null);
  const [ackChecked, setAckChecked] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(false);
  /** Nachfragen aufgegeben (Lauf haengt) — „Strecke pruefen" wird wieder frei. */
  const [timedOut, setTimedOut] = useState(false);

  const vorhanden = FUNNEL_PAGE_TYPES.filter((t) => pages[t]);

  const loadLatest = useCallback(async (): Promise<FunnelCheckResult | null | undefined> => {
    const res = await getFunnelCheck(webinarId);
    if (!res.success) {
      setLoadError(res.error || 'Letzte Prüfung konnte nicht geladen werden');
      return undefined;
    }
    setLoadError(null);
    setAktuellerHash(res.data?.scopeHash ?? null);
    setLetzterCheck(res.data?.letzter ?? null);
    setFrischeRegeln(res.data?.regeln ?? null);
    setProduktReferenz(res.data?.produktReferenz);
    return res.data?.check ?? null;
  }, [webinarId]);

  useEffect(() => {
    let cancelled = false;
    setCheck(undefined);
    setTimedOut(false);
    (async () => {
      const c = await loadLatest();
      if (!cancelled) setCheck(c === undefined ? null : c);
    })();
    return () => {
      cancelled = true;
    };
  }, [loadLatest, reloadKey]);

  const running = starting || (check?.status === 'running' && !timedOut);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [w, st] = await Promise.all([
        fetchData<{
          title?: string;
          display_title?: string | null;
          plx_product?: string | null;
          plx_product_name?: string | null;
        }>(`/api/webinar-management/webinars/${encodeURIComponent(webinarId)}`),
        getFunnelSettings(webinarId),
      ]);
      if (cancelled) return;
      const d = w.success ? w.data : null;
      // Ohne Webinar-Daten lieber der allgemeine Satz als „kein Produkt eingetragen“ (waere falsch).
      setBezug(
        d
          ? {
              webinarTitel: d.display_title || d.title || null,
              produktName: d.plx_product_name ?? null,
              produktCode: d.plx_product ?? null,
              upsellProdukt: st.success ? (st.data?.upsellProdukt ?? null) : null,
            }
          : null,
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [webinarId, reloadKey]);

  // Pruefstand nach oben melden (undefined = laedt noch → nichts melden).
  useEffect(() => {
    if (check !== undefined && onCheckChange) onCheckChange(check);
  }, [check, onCheckChange]);

  /** Letzte Speicherung irgendeiner Seite der Strecke (UTC-Bytes, siehe LpFunnelPageDialog). */
  const letzteSpeicherung = useMemo(() => {
    const werte = FUNNEL_PAGE_TYPES.map((t) => pages[t]?.updated_at).filter(
      (v): v is string => !!v,
    );
    return werte.sort().pop() ?? null;
  }, [pages]);

  // Sekundenzaehler fuer den Spinner, solange gerechnet wird.
  useEffect(() => {
    if (!running) {
      startedAt.current = null;
      return undefined;
    }
    if (startedAt.current === null) startedAt.current = Date.now();
    const t = setInterval(
      () => setElapsed(Math.round((Date.now() - (startedAt.current ?? Date.now())) / 1000)),
      1000,
    );
    return () => clearInterval(t);
  }, [running]);

  // Laeuft der Check serverseitig noch (POST kam mit status=running zurueck oder ein anderer
  // Bediener prueft gerade), bis zum Ergebnis nachfragen — hoechstens MAX_WARTEN_S.
  useEffect(() => {
    if (check?.status !== 'running' || starting || timedOut) return undefined;
    let cancelled = false;
    const t = setTimeout(async () => {
      if (startedAt.current !== null && Date.now() - startedAt.current > MAX_WARTEN_S * 1000) {
        setTimedOut(true);
        setLoadError(
          `Die Prüfung läuft seit über ${MAX_WARTEN_S} s — bitte neu laden oder erneut prüfen.`,
        );
        return;
      }
      const c = await loadLatest();
      if (!cancelled && c !== undefined) setCheck(c);
    }, POLL_MS);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [check, starting, timedOut, loadLatest]);

  const runCheck = async () => {
    setStarting(true);
    setTimedOut(false);
    setElapsed(0);
    startedAt.current = Date.now();
    setPublishError(null);
    setPublished(null);
    setLoadError(null);
    const res = await startFunnelCheck(webinarId);
    setStarting(false);
    if (!res.success || !res.data?.check) {
      setLoadError(res.error || 'Prüfung konnte nicht gestartet werden');
      return;
    }
    setCheck(res.data.check);
    setAktuellerHash(res.data.check.scopeHash);
    setFrischeRegeln(res.data.check.regeln);
    if (res.data.produktReferenz !== undefined) setProduktReferenz(res.data.produktReferenz);
  };

  // Frische Regeln gelten fuer den Check des aktuellen Stands; sonst die gespeicherten.
  const regelnAnzeige = useMemo(
    () =>
      check && frischeRegeln && check.scopeHash === aktuellerHash
        ? frischeRegeln
        : check?.regeln || [],
    [check, frischeRegeln, aktuellerHash],
  );
  const regelFehler = useMemo(
    () => regelnAnzeige.filter((r) => r.schwere === 'fehler'),
    [regelnAnzeige],
  );
  const kiRot = useMemo(() => (check?.ki || []).filter((k) => k.schwere === 'rot'), [check]);
  const fertig = !!check && check.status !== 'running';
  const kiFehlversuch = fertig && !!check?.kiAktiv && check?.status === 'error';
  const regelnOk = fertig && regelFehler.length === 0;
  const direkt = regelnOk && check?.status === 'done' && !(check.kiAktiv && kiRot.length > 0);
  const mitQuittungBefunde =
    regelnOk && check?.status === 'done' && !!check.kiAktiv && kiRot.length > 0;
  const mitQuittungFehlversuch = regelnOk && kiFehlversuch;

  const publish = async (ack: AckReason | null) => {
    if (!check) return;
    setPublishBusy(true);
    setPublishError(null);
    setPublished(null);
    if (ack) {
      const a = await ackFunnelCheck(webinarId, check.checkId, ack);
      if (!a.success) {
        setPublishBusy(false);
        setPublishError({
          text: `Quittung abgelehnt: ${a.error || 'unbekannter Fehler'}`,
          details: null,
        });
        return;
      }
    }
    const res = await publishFunnelStrecke(webinarId, check.scopeHash);
    setPublishBusy(false);
    setAckDialog(null);
    setConfirmPublish(false);
    setAckChecked(false);
    if (!res.success || !res.data) {
      setPublishError({
        text: res.error || 'Veröffentlichen fehlgeschlagen',
        details: res.details ?? null,
      });
      // Der Server kennt den Grund (Hash veraltet, Pruefung fehlt) — Stand neu holen.
      const c = await loadLatest();
      if (c !== undefined) setCheck(c);
      return;
    }
    setPublished(res.data);
    onPublished(
      `Strecke veröffentlicht: ${res.data.pages.map((p) => FUNNEL_LABELS[p.pageType]).join(', ')}`,
      res.data.warnings || [],
    );
  };

  if (check === undefined && !loadError) return <Loading label="Letzte Prüfung laden …" />;

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Alert severity="info">
        Geprüft und veröffentlicht wird immer die ganze Strecke (
        {vorhanden.map((t) => FUNNEL_LABELS[t]).join(', ') || 'noch keine Seite'}) im gespeicherten
        Stand, zusammen mit Stichtag, Upsell-Produkt und den Webinar-Daten. Jede Änderung danach
        verlangt eine neue Prüfung. Zwei Prüfungen laufen: <b>feste Regeln</b> (Aufbau, Formular,
        Platzhalter, Termine) und die <b>KI</b> (liest die Texte gegen Webinar und Produkt).
      </Alert>
      {loadError && <Alert severity="error">{loadError}</Alert>}
      {produktReferenz !== undefined && (
        <Typography variant="body2" color="text.secondary" data-testid="ki-referenzen">
          {referenzZeile(produktReferenz)}
        </Typography>
      )}

      <Box display="flex" alignItems="center" gap={2}>
        <Button
          variant="contained"
          onClick={runCheck}
          disabled={running || publishBusy || !vorhanden.length}
        >
          {running ? 'Prüft …' : 'Strecke prüfen'}
        </Button>
        {running && (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              {elapsed} s — Regeln und KI-Prüfung, dauert bis zu 120 s
            </Typography>
          </Box>
        )}
      </Box>

      {fertig && check && (
        <>
          <Typography variant="caption" color="text.secondary">
            <Chip
              size="small"
              color="success"
              variant="outlined"
              label="Prüfung aktuell — seit der Prüfung nichts am Inhalt geändert"
              sx={{ mr: 1 }}
            />
            Geprüft am {formatBerlin(check.finishedAt || check.createdAt)}
            {check.createdBy ? ` von ${check.createdBy}` : ''}
            {check.durationMs != null ? ` · ${Math.round(check.durationMs / 1000)} s` : ''}
            {' · '}
            {check.kiAktiv ? 'mit KI-Prüfung' : 'KI-Prüfung aus (nur feste Regeln)'}
            {istAdmin && (
              <>
                {' · '}Stand {shortHash(check.scopeHash)}
                {check.model ? ` · Modell ${check.model}` : ''}
              </>
            )}
          </Typography>

          <RegelListe regeln={regelnAnzeige} />

          {check.kiAktiv && check.status === 'done' && (
            <KiListe befunde={check.ki || []} bezug={bezug} />
          )}
          {kiFehlversuch && (
            <Alert severity="warning">
              <b>KI-Prüfung nicht verfügbar.</b> {check.errorMessage || 'Kein Ergebnis.'} Erneut
              prüfen — oder bewusst ohne KI veröffentlichen (wird mit deinem Namen protokolliert).
            </Alert>
          )}
          {check.status === 'error' && !check.kiAktiv && (
            <Alert severity="error">
              Prüfung fehlgeschlagen: {check.errorMessage || 'unbekannter Fehler'}
            </Alert>
          )}

          <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {regelFehler.length > 0 && (
              <Typography variant="body2" color="error">
                {regelFehler.length} Regel-Fehler — Veröffentlichen gesperrt, bis sie behoben und
                neu geprüft sind.
              </Typography>
            )}
            <Box display="flex" gap={1} flexWrap="wrap">
              <Button
                variant="contained"
                color="success"
                disabled={!direkt || publishBusy}
                onClick={() => setConfirmPublish(true)}
              >
                {publishBusy && !ackDialog ? 'Veröffentlicht …' : 'Strecke veröffentlichen'}
              </Button>
              {mitQuittungBefunde && (
                <Button
                  variant="outlined"
                  color="error"
                  disabled={publishBusy}
                  onClick={() => setAckDialog('ki_findings')}
                >
                  Trotzdem veröffentlichen ({kiRot.length} zum Korrigieren) …
                </Button>
              )}
              {mitQuittungFehlversuch && (
                <Button
                  variant="outlined"
                  color="warning"
                  disabled={publishBusy}
                  onClick={() => setAckDialog('ki_unavailable')}
                >
                  Ohne KI-Prüfung veröffentlichen
                </Button>
              )}
            </Box>
          </Paper>
        </>
      )}
      {check === null && !running && letzterCheck && letzterCheck.scopeHash !== aktuellerHash && (
        <Alert severity="warning" data-testid="pruefung-veraltet">
          <Chip
            size="small"
            color="warning"
            label="Prüfung veraltet — bitte neu prüfen"
            sx={{ mr: 1 }}
          />
          Letzte Prüfung am {formatBerlin(letzterCheck.createdAt)}
          {letzteSpeicherung
            ? `, danach gespeichert am ${formatUtcAsBerlin(letzteSpeicherung)}`
            : ''}{' '}
          (Seite oder Einstellungen geändert). Ohne neue Prüfung lässt sich nicht veröffentlichen.
        </Alert>
      )}
      {check === null &&
        !running &&
        !(letzterCheck && letzterCheck.scopeHash !== aktuellerHash) && (
          <Alert severity="warning">
            Für den aktuellen Stand gibt es keine Prüfung — „Strecke prüfen“ starten. Ohne Prüfung
            lässt sich nicht veröffentlichen.
            <NurAdmin> Stand {shortHash(aktuellerHash)}.</NurAdmin>
          </Alert>
        )}

      {publishError && (
        <Alert severity="error">
          <b>{publishError.text}</b>
          {publishError.details?.code && PUBLISH_FEHLER_HINWEIS[publishError.details.code] && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {PUBLISH_FEHLER_HINWEIS[publishError.details.code]}
            </Typography>
          )}
          {!!publishError.details?.regeln?.length && (
            <Box sx={{ mt: 1 }}>
              <RegelListe regeln={publishError.details.regeln} />
            </Box>
          )}
          {!!publishError.details?.ki?.length && (
            <Box sx={{ mt: 1 }}>
              <KiListe befunde={publishError.details.ki} bezug={bezug} />
            </Box>
          )}
        </Alert>
      )}
      {published && (
        <Alert severity="success">
          <b>Veröffentlicht</b>
          <NurAdmin> (Stand {shortHash(published.scopeHash)})</NurAdmin>
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
            {published.pages.map((p) => (
              <li key={p.pageType}>
                {FUNNEL_LABELS[p.pageType]}:{' '}
                <Link href={p.url} target="_blank" rel="noopener noreferrer">
                  {p.url}
                </Link>
              </li>
            ))}
          </Box>
          {published.warnings.length > 0 && (
            <>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <b>Hinweise beim Veröffentlichen:</b>
              </Typography>
              <WarningList warnings={published.warnings} />
            </>
          )}
        </Alert>
      )}

      <ConfirmDialog
        open={confirmPublish}
        title="Strecke veröffentlichen?"
        confirmLabel="Veröffentlichen"
        busy={publishBusy}
        onConfirm={() => publish(null)}
        onCancel={() => setConfirmPublish(false)}
      >
        <Typography variant="body2">
          {vorhanden.map((t) => FUNNEL_LABELS[t]).join(', ')} gehen im geprüften Stand ins Netz.
          Verlinkt werden sie dadurch noch nirgends — die Bestellseite wird erst im Schritt „Live
          schalten“ zur Bestelladresse des Webinars.
        </Typography>
      </ConfirmDialog>

      {/* Rote KI-Befunde: Tipp-Bestätigung mit den Befunden in Kurzform (Persona-Runde 2 — ein
          falscher Produktname ging per einfachem Häkchen live). Server-Weg unverändert: Quittung
          ki_findings für genau diesen Check, dann Publish. */}
      <GefahrBestaetigung
        open={ackDialog === 'ki_findings'}
        titel="Trotzdem veröffentlichen?"
        knopfText="Trotzdem veröffentlichen"
        bestaetigungsWort="geprüft"
        laeuft={publishBusy}
        onBestaetigen={() => void publish('ki_findings')}
        onAbbrechen={() => setAckDialog(null)}
        wirkung={
          <>
            <Typography variant="body2" gutterBottom>
              {kiRot.length === 1 ? 'Diese Stelle geht' : `Diese ${kiRot.length} Stellen gehen`} so
              ins Netz:
            </Typography>
            <Box component="ul" sx={{ m: 0, mb: 1, pl: 2.5 }} data-testid="rote-befunde-kurz">
              {kiRot.map((b, i) => (
                <li key={`${b.zitat}-${i}`}>
                  <Typography variant="body2">
                    <b>{FUNNEL_LABELS[b.seite]}</b> · {KATEGORIE_LABEL[b.kategorie] || b.kategorie}:
                    „{b.zitat.length > 90 ? `${b.zitat.slice(0, 90)} …` : b.zitat}“ —{' '}
                    {anzeigeText(b.begruendung)}
                  </Typography>
                </li>
              ))}
            </Box>
            <Typography variant="body2">
              Nur bestätigen, wenn die KI sich irrt oder es so gewollt ist (z. B. Rabatt im Upsell).
              Im Zweifel Till fragen. Gilt nur für diesen Stand und wird mit deinem Namen
              gespeichert.
            </Typography>
          </>
        }
      />

      <ConfirmDialog
        open={ackDialog === 'ki_unavailable'}
        title="Ohne KI-Prüfung veröffentlichen?"
        confirmLabel="Trotzdem veröffentlichen"
        danger
        busy={publishBusy}
        confirmDisabled={!ackChecked}
        onConfirm={() => ackDialog && publish(ackDialog)}
        onCancel={() => {
          setAckDialog(null);
          setAckChecked(false);
        }}
      >
        <Typography variant="body2" gutterBottom>
          {`Die KI-Prüfung ist für diesen Stand fehlgeschlagen${check?.errorMessage ? `: ${check.errorMessage}` : ''}.`}{' '}
          Die Quittung gilt nur für genau diesen geprüften Stand und wird mit deinem Namen
          protokolliert.
        </Typography>
        <FormControlLabel
          control={
            <Checkbox checked={ackChecked} onChange={(e) => setAckChecked(e.target.checked)} />
          }
          label="Ich veröffentliche bewusst ohne KI-Prüfung."
        />
      </ConfirmDialog>
    </Box>
  );
}

/**
 * Anzeige-Übersetzung der Server-Texte (die Texte selbst bleiben unverändert): „Premiere“ ist
 * Technik-Sprache für den Webinar-Termin. Gross geschriebene Platzhalter wie PREMIERE_DATUM in
 * geschweiften Klammern bleiben stehen — die tippt man so ein.
 */
export function anzeigeText(t: string): string {
  return t.replace(/Premiere/g, 'Webinar-Termin');
}

/**
 * „Was tun?“ je Regel (Namen aus node-app/src/services/lpFunnel/regeln.ts). Der Regeltext des
 * Servers bleibt stehen (er nennt die Stelle); hier steht, was Marketing damit macht.
 */
const REGEL_TUN: Record<string, string> = {
  seite_fehlt: 'Im Schritt „Seiten hochladen“ die Bestellseite anlegen.',
  formular_anzahl:
    'Das Bestellformular muss genau einmal auf der Bestellseite stehen: unter „Erweitert → HTML“ den Platz <div data-lp-order-form></div> genau einmal setzen.',
  kampagne_fehlt: 'Im Schritt „Einstellungen“ die PL-X-Kampagne eintragen.',
  kampagne_ohne_formular:
    'Im Schritt „Einstellungen“ die Bestell-Kampagne eintragen (Art „Standard“ in PL-X), nicht die Upsell-Kampagne. Unter dem Feld steht dann die WKZ.',
  kampagne_produkt:
    'Die Kampagne gehört zu einem anderen Produkt — im Schritt „Einstellungen“ die Kampagne dieses Produkts eintragen.',
  kampagne_produkt_unsicher:
    'Im Schritt „Einstellungen“ unter dem Feld WKZ und Produkt lesen und mit dem Angebot der Seite vergleichen.',
  wkz_ohne_config:
    'Unter Bestellungen › Konfiguration die WKZ mit Produkt und Preis anlegen. Ohne Zugriff dort: Till Bescheid geben. Veröffentlichen geht trotzdem.',
  kampagne_ungeprueft:
    'PL-X war nicht erreichbar — später erneut prüfen oder WKZ und Produkt in PL-X selbst kontrollieren.',
  widget_anzahl:
    'Das Upsell-Formular muss genau einmal auf der Upsell-Seite stehen: <div data-lp-upsell></div>.',
  pflichtmarker_in_abschnitt:
    'Das Formular steht in einem Bereich, der nur zeitweise sichtbar ist — dann gäbe es zeitweise kein Formular. Aus dem Abschnitt herausnehmen.',
  marker_form:
    'In der Vorschau prüfen, ob Formular bzw. Countdown an der richtigen Stelle erscheinen.',
  script_im_body:
    'Das genannte Skript darf auf unseren Seiten nicht laufen (Grund steht dabei): unter „Erweitert → HTML“ entfernen oder die Seite ohne dieses Skript neu hochladen.',
  lpm_link: 'Der Link zeigt noch auf eine alte PL-X-Landingpage — durch die neue Adresse ersetzen.',
  lpm_asset:
    'Die Datei wird noch von der alten PL-X-Landingpage geladen — besser beim Hochladen mitliefern.',
  fremd_pixel: 'Messpunkt einer fremden Seite — wenn nicht gewollt, entfernen.',
  token_unaufloesbar:
    'Ein Platzhalter lässt sich nicht ersetzen — meist fehlt der Stichtag (Schritt „Einstellungen“) oder er steht an einer Stelle, an der er nicht geht.',
  abschnitt_wert: 'Unter „Erweitert → HTML“ den Wert des Abschnitts korrigieren.',
  abschnitt_ohne_gegenstueck:
    'Für einen Teil der Besucher fehlt hier Text. Gegenstück ergänzen oder bewusst so lassen.',
  abschnitt_ohne_stichtag:
    'Die Seite hat Texte für vor/nach dem Stichtag, aber es ist keiner eingetragen — Schritt „Einstellungen“.',
  platzhalter_unbekannt: 'Schreibweise prüfen (geschweifte Klammern, Großbuchstaben).',
  wochentag_falsch:
    'Text an den echten Termin anpassen — oder einen Zeit-Platzhalter verwenden, der sich selbst richtig füllt.',
  datum_fremd:
    'Text an den echten Termin bzw. Stichtag anpassen — oder einen Zeit-Platzhalter verwenden.',
  uhrzeit_fremd: 'Uhrzeit an den echten Termin bzw. Stichtag anpassen.',
  ueberschrift_doppelt: 'Eine der beiden Überschriften entfernen.',
  produktbild_bindung:
    'Unter „Erweitert → Bearbeiten“ das Bild anklicken und aus den Produkt-Medien neu wählen.',
  fremdes_webinar:
    'Der Text stammt vermutlich aus einem anderen Webinar (Kopie einer älteren Seite) — Stelle ersetzen oder entfernen.',
  fremdes_webinar_aus:
    'Die Prüfung auf Reste anderer Webinare konnte nicht laufen — bitte selbst auf Begriffe älterer Webinare achten.',
};

const GRUPPE_TITEL: Record<RegelBefund['schwere'], string> = {
  fehler: 'Muss behoben werden — sonst kein Veröffentlichen',
  warnung: 'Bitte prüfen — veröffentlichen geht trotzdem',
  info: 'Zur Info',
};

function RegelListe({ regeln }: { regeln: RegelBefund[] }) {
  if (!regeln.length) {
    return (
      <Alert severity="success">
        Feste Regeln (Aufbau, Formular, Platzhalter): alles in Ordnung.
      </Alert>
    );
  }
  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Typography variant="subtitle2">
        Feste Regeln (Aufbau, Formular, Platzhalter, Termine)
      </Typography>
      {(['fehler', 'warnung', 'info'] as const).map((schwere) => {
        const inGruppe = regeln.filter((r) => r.schwere === schwere);
        if (!inGruppe.length) return null;
        return (
          <Paper key={schwere} variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              {GRUPPE_TITEL[schwere]} ({inGruppe.length})
            </Typography>
            <Box display="flex" flexDirection="column" gap={0.75}>
              {SEITEN.map((s) => {
                const liste = inGruppe.filter((r) => r.seite === s);
                if (!liste.length) return null;
                const dateien = liste.filter((r) => r.regel === 'asset_fehlt');
                const rest = liste.filter((r) => r.regel !== 'asset_fehlt');
                return (
                  <Box key={s}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {seiteLabel(s)}
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={0.75} mt={0.25}>
                      {rest.map((r, i) => (
                        <Alert
                          key={`${r.regel}-${i}`}
                          severity={SCHWERE_SEVERITY[r.schwere]}
                          sx={{ py: 0 }}
                        >
                          <b>{SCHWERE_LABEL[r.schwere]}:</b> {anzeigeText(r.text)}
                          {REGEL_TUN[r.regel] && (
                            <Typography variant="body2" sx={{ mt: 0.25 }}>
                              <b>Was tun?</b> {REGEL_TUN[r.regel]}
                            </Typography>
                          )}
                          {r.stelle && (
                            <Typography
                              variant="caption"
                              display="block"
                              sx={{ fontFamily: 'monospace', wordBreak: 'break-all', mt: 0.25 }}
                            >
                              {r.stelle}
                            </Typography>
                          )}
                          {r.link && (
                            <Link
                              href={r.link}
                              target="_blank"
                              rel="noopener"
                              variant="body2"
                              sx={{ display: 'inline-block', mt: 0.25 }}
                            >
                              Dort beheben (neuer Tab)
                            </Link>
                          )}
                        </Alert>
                      ))}
                      {dateien.length > 0 && (
                        <Alert
                          severity={SCHWERE_SEVERITY[schwere]}
                          sx={{ py: 0 }}
                          data-testid={`dateien-fehlten-${s}`}
                        >
                          <b>
                            {dateien.length} Datei{dateien.length === 1 ? '' : 'en'} fehlte
                            {dateien.length === 1 ? '' : 'n'} beim Hochladen
                          </b>
                          <Typography variant="body2" sx={{ mt: 0.25 }}>
                            Diese Dateien waren nicht im Upload. Benutzt die Seite sie, fehlt dort
                            das Bild bzw. die Gestaltung; manche sind nur in Gestaltungsregeln
                            erwähnt und fallen nicht auf. <b>Was tun?</b> In der Vorschau prüfen;
                            was sichtbar fehlt, im Schritt „Seiten hochladen“ mitliefern (ganzer
                            Ordner oder „Fehlende Dateien nachreichen“).
                          </Typography>
                          <Box component="details" sx={{ mt: 0.5 }}>
                            <Box component="summary" sx={{ cursor: 'pointer', fontSize: 13 }}>
                              Dateien anzeigen
                            </Box>
                            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                              {dateien.map((r, i) => (
                                <li key={`${r.stelle}-${i}`}>
                                  <Typography
                                    variant="caption"
                                    sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                                  >
                                    {r.stelle || r.text}
                                  </Typography>
                                </li>
                              ))}
                            </Box>
                          </Box>
                        </Alert>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
}

/** Bezugsgroessen der KI-Pruefung in Worten (nur Anzeige, Quelle: Webinar-Grunddaten + Einstellungen). */
export interface KiBezug {
  webinarTitel: string | null;
  produktName: string | null;
  produktCode: string | null;
  upsellProdukt: string | null;
}

/** Ein Satz je Gruppe statt je Befund (30.10.: kompakter). */
const KI_TUN_GRUPPE: Record<KiBefund['schwere'], string> = {
  rot: 'Unter „Erweitert → Bearbeiten“ korrigieren, speichern, neu prüfen. Irrt sich die KI oder ist es gewollt: „Trotzdem veröffentlichen“.',
  gelb: 'Kurz ansehen und bei Bedarf korrigieren — sperrt nicht.',
};
const KI_TUN_FREMD =
  'Steht das richtige Produkt in den Webinar-Grunddaten? Verkauft der Upsell bewusst ein anderes Produkt: in „Einstellungen“ bei „Upsell-Produkt“ eintragen.';

function bezugSatz(b: KiBezug | null): string {
  if (!b) return 'Die KI vergleicht mit dem Webinar und seinem Produkt.';
  const teile: string[] = [];
  if (b.produktName || b.produktCode)
    teile.push(
      `das Produkt „${b.produktName || b.produktCode}“${b.produktName && b.produktCode ? ` (${b.produktCode})` : ''} aus den Webinar-Grunddaten`,
    );
  else teile.push('kein Produkt — in den Webinar-Grunddaten ist keins eingetragen');
  if (b.upsellProdukt) teile.push(`als Upsell „${b.upsellProdukt}“ aus den Einstellungen`);
  if (b.webinarTitel) teile.push(`das Webinar „${b.webinarTitel}“`);
  return `Das System erwartet ${teile.join(', ')}.`;
}

/** Eine Befund-Zeile: Seite · Etikett, Zitat einzeilig, ein Satz, Vorschlag — Langes per „mehr“ aufklappbar. */
function KiZeile({ b }: { b: KiBefund }) {
  const [offen, setOffen] = useState(false);
  // „mehr“ haengt am tatsaechlichen Abschneiden (Breite!), nicht an der Zeichenzahl — auf schmalem Panel kappt schon
  // ein 140-Zeichen-Satz (Codex 30.10.). Zeichenzahl nur als Rueckfall, wo nichts messbar ist (jsdom).
  const zitatRef = useRef<HTMLElement | null>(null);
  const grundRef = useRef<HTMLElement | null>(null);
  const vorschlagRef = useRef<HTMLElement | null>(null);
  const [gekappt, setGekappt] = useState(false);
  useLayoutEffect(() => {
    if (offen) return undefined;
    const miss = () => {
      const z = zitatRef.current;
      const hoch = (el: HTMLElement | null) => !!el && el.scrollHeight > el.clientHeight + 1;
      setGekappt(
        !!z && z.scrollWidth > z.clientWidth + 1
          ? true
          : hoch(grundRef.current) || hoch(vorschlagRef.current),
      );
    };
    miss();
    window.addEventListener('resize', miss);
    return () => window.removeEventListener('resize', miss);
  }, [offen, b]);
  const lang =
    gekappt || b.begruendung.length > 100 || b.vorschlag.length > 100 || b.zitat.length > 70;
  const kappe = offen
    ? {}
    : {
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical' as const,
        overflow: 'hidden',
      };
  return (
    <Box
      sx={{
        py: 0.75,
        '&:not(:last-of-type)': { borderBottom: '1px solid', borderColor: 'divider' },
      }}
    >
      <Typography variant="body2">
        <b>{FUNNEL_LABELS[b.seite]}</b> · {KATEGORIE_LABEL[b.kategorie] || b.kategorie}
      </Typography>
      <Typography
        ref={zitatRef}
        variant="body2"
        title={b.zitat}
        sx={{
          fontStyle: 'italic',
          color: 'text.secondary',
          ...(offen ? {} : { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
        }}
      >
        „{b.zitat}“
      </Typography>
      <Typography ref={grundRef} variant="body2" sx={kappe}>
        {anzeigeText(b.begruendung)}
      </Typography>
      {b.vorschlag && (
        <Typography ref={vorschlagRef} variant="body2" sx={kappe}>
          → {anzeigeText(b.vorschlag)}
        </Typography>
      )}
      {(lang || offen) && (
        <Link component="button" variant="caption" onClick={() => setOffen(!offen)}>
          {offen ? 'weniger' : 'mehr'}
        </Link>
      )}
    </Box>
  );
}

function KiGruppe({ schwere, liste }: { schwere: KiBefund['schwere']; liste: KiBefund[] }) {
  if (!liste.length) return null;
  const sortiert = [...liste].sort(
    (a, b) => FUNNEL_PAGE_TYPES.indexOf(a.seite) - FUNNEL_PAGE_TYPES.indexOf(b.seite),
  );
  return (
    <Alert
      severity={schwere === 'rot' ? 'error' : 'warning'}
      sx={{ '& .MuiAlert-message': { width: '100%' } }}
    >
      <Box display="flex" gap={0.75} alignItems="center" flexWrap="wrap">
        <Chip
          size="small"
          label={schwere === 'rot' ? 'korrigieren' : 'bitte prüfen'}
          color={schwere === 'rot' ? 'error' : 'warning'}
        />
        <Typography variant="body2">{KI_TUN_GRUPPE[schwere]}</Typography>
      </Box>
      {schwere === 'rot' && liste.some((b) => b.kategorie === 'fremd') && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {KI_TUN_FREMD}
        </Typography>
      )}
      <Box sx={{ mt: 0.5 }}>
        {sortiert.map((b, i) => (
          <KiZeile key={`${b.seite}-${b.zitat}-${i}`} b={b} />
        ))}
      </Box>
    </Alert>
  );
}

/** Skript-Hinweise: neutral, eingeklappt, sperren nie (30.10., Till: „Entscheidung vom Marketing — Hinweis, nicht blocken“). */
function SkriptHinweise({ liste }: { liste: KiBefund[] }) {
  const [offen, setOffen] = useState(false);
  if (!liste.length) return null;
  return (
    <Alert
      severity="info"
      data-testid="ki-skript-hinweise"
      sx={{ '& .MuiAlert-message': { width: '100%' } }}
    >
      <Typography variant="body2">
        <b>Hinweise zu Seitenskripten ({liste.length})</b> — sperren nicht. Ob ein Skript bleibt,
        entscheidet ihr.{' '}
        <Link component="button" variant="body2" onClick={() => setOffen(!offen)}>
          {offen ? 'ausblenden' : 'anzeigen'}
        </Link>
      </Typography>
      {offen &&
        liste.map((b, i) => (
          <Typography key={`${b.seite}-${i}`} variant="body2" sx={{ mt: 0.5 }}>
            <b>{FUNNEL_LABELS[b.seite]}</b>: {anzeigeText(b.begruendung)}
          </Typography>
        ))}
    </Alert>
  );
}

function KiListe({ befunde, bezug }: { befunde: KiBefund[]; bezug?: KiBezug | null }) {
  const skripte = befunde.filter((b) => b.kategorie === 'skript');
  const rot = befunde.filter((b) => b.kategorie !== 'skript' && b.schwere === 'rot');
  const gelb = befunde.filter((b) => b.kategorie !== 'skript' && b.schwere !== 'rot');
  if (!rot.length && !gelb.length) {
    return (
      <Box display="flex" flexDirection="column" gap={1}>
        <Alert severity="success">KI-Prüfung: keine Auffälligkeiten in den Texten.</Alert>
        <SkriptHinweise liste={skripte} />
      </Box>
    );
  }
  const zaehlung = [
    rot.length ? `${rot.length} korrigieren` : '',
    gelb.length ? `${gelb.length} bitte prüfen` : '',
  ].filter(Boolean);
  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Typography variant="subtitle2" data-testid="ki-zaehlung">
        KI-Prüfung: {zaehlung.join(' · ')}
      </Typography>
      <Typography variant="caption" color="text.secondary" data-testid="ki-bezug">
        {bezugSatz(bezug ?? null)}
      </Typography>
      <KiGruppe schwere="rot" liste={rot} />
      <KiGruppe schwere="gelb" liste={gelb} />
      <SkriptHinweise liste={skripte} />
    </Box>
  );
}
